if (!customElements.get('recently-viewed')) {
  customElements.define(
    'recently-viewed',
    class extends HTMLElement {
      constructor() {
        super();
        this.STORAGE_KEY = 'revolor_recently_viewed';
        this.STORAGE_VERSION = 2;
        this.MAX_ITEMS = 10;
        this.MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000;
        this.SECTION_ID = 'recently-viewed-card';
        this.productsToShow = parseInt(this.dataset.productsToShow) || 8;
        this.currentHandle = this.dataset.currentHandle;
        this.track = this.querySelector('.recently-viewed__track');
        this.abortController = null;
      }

      connectedCallback() {
        this.storeCurrentHandle();
        this.renderProducts();
      }

      disconnectedCallback() {
        if (this.abortController) {
          this.abortController.abort();
          this.abortController = null;
        }
      }

      /* ── localStorage (versioned, timestamped) ── */

      getStoredItems() {
        try {
          const raw = localStorage.getItem(this.STORAGE_KEY);
          if (!raw) return [];
          const parsed = JSON.parse(raw);
          if (parsed.version !== this.STORAGE_VERSION) {
            localStorage.removeItem(this.STORAGE_KEY);
            return [];
          }
          const now = Date.now();
          return parsed.items.filter((item) => now - item.ts < this.MAX_AGE_MS);
        } catch {
          return [];
        }
      }

      saveItems(items) {
        try {
          localStorage.setItem(
            this.STORAGE_KEY,
            JSON.stringify({ version: this.STORAGE_VERSION, items })
          );
        } catch {
          // localStorage full or disabled
        }
      }

      storeCurrentHandle() {
        if (!this.currentHandle) return;
        let items = this.getStoredItems();
        items = items.filter((item) => item.handle !== this.currentHandle);
        items.unshift({ handle: this.currentHandle, ts: Date.now() });
        items = items.slice(0, this.MAX_ITEMS);
        this.saveItems(items);
      }

      /* ── Rendering via Section Rendering API ── */

      async renderProducts() {
        const handles = this.getStoredItems()
          .filter((item) => item.handle !== this.currentHandle)
          .slice(0, this.productsToShow)
          .map((item) => item.handle);

        if (handles.length === 0) return;

        this.abortController = new AbortController();
        const signal = this.abortController.signal;

        const results = await Promise.allSettled(
          handles.map((handle) =>
            fetch(`/products/${handle}?section_id=${this.SECTION_ID}`, { signal })
              .then((r) => (r.ok ? r.text() : null))
          )
        );

        const cards = results
          .filter((r) => r.status === 'fulfilled' && r.value)
          .map((r) => r.value);

        if (cards.length === 0) return;

        this.track.innerHTML = cards.join('');
        this.style.display = '';
        setTimeout(() => this.createScrollIndicator(), 150);
      }

      /* ── Scroll indicator (mobile) ── */

      createScrollIndicator() {
        if (this.querySelector('.rv-indicator')) return;

        const track = this.track;
        const indicator = document.createElement('div');
        indicator.className = 'rv-indicator';
        const fill = document.createElement('div');
        fill.className = 'rv-indicator__fill';
        fill.style.cssText =
          'display:block;position:absolute;top:0;left:0;height:100%;width:20%;background:#000;border-radius:2px;will-change:transform;transition:transform .15s ease-out';
        indicator.appendChild(fill);
        this.appendChild(indicator);

        let indicatorReady = false;
        const update = () => {
          const sw = track.scrollWidth;
          const cw = track.clientWidth;
          if (sw <= cw) {
            fill.style.width = '100%';
            fill.style.transform = 'translateX(0)';
            return;
          }
          const ratio = cw / sw;
          if (!indicatorReady) {
            fill.style.width = ratio * 100 + '%';
            indicatorReady = true;
          }
          const pos = track.scrollLeft / (sw - cw);
          const maxOffset = (1 / ratio - 1) * 100;
          fill.style.transform = `translateX(${pos * maxOffset}%)`;
        };

        track.addEventListener('scroll', update, { passive: true });
        requestAnimationFrame(update);
      }
    }
  );
}
